/**
 * Main Header Component
 *
 * The primary navigation header for the American Police Training platform.
 * Includes user authentication controls, navigation menu, mobile responsiveness,
 * and user profile management. Handles all header-related functionality.
 *
 * @author Andres Nava
 * @version 1.0.0
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, useReducer } from 'react';
// import { logger } from "../../utils/logger";
import { AppBar, Toolbar, Typography, Button, IconButton, Box, Avatar, Menu, MenuItem, Badge, Drawer, List, ListItem, ListItemIcon, ListItemText, Collapse, useMediaQuery, Tooltip, Divider, Chip, Popover, ClickAwayListener, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Menu as MenuIcon, Notifications as NotificationsIcon, AccountCircle, Dashboard as DashboardIcon, Settings as SettingsIcon, Logout as LogoutIcon, Home as HomeIcon, Store as StoreIcon, Extension as ExtensionIcon, PhotoLibrary as GalleryIcon, Search as SearchIcon, Leaderboard as LeaderboardIcon, Article as BlogIcon, LightMode as LightModeIcon, DarkMode as DarkModeIcon, ExpandLess, ExpandMore, Logout, ShoppingCart, Computer, PhotoCamera, Business, Info, EmojiEvents, AdminPanelSettings, Favorite, Person, Login, Message as MessageIcon, Pets, Close as CloseIcon, Hotel as HotelIcon, Event as EventIcon, Place as PlaceIcon, MenuBook as MenuBookIcon, People as PeopleIcon, Restaurant as RestaurantIcon, WorkOutline as WorkOutlineIcon, AutoStories as AutoStoriesIcon, Verified as VerifiedIcon, MailOutline as MailOutlineIcon, PrivacyTip as PrivacyTipIcon, Description as DescriptionIcon, Cookie as CookieIcon, Web as WebIcon, ChatBubbleOutline as ChatBubbleOutlineIcon, PhoneIphone as PhoneIphoneIcon, Calculate as CalculateIcon, Handyman as HandymanIcon, Assessment as AssessmentIcon, FactCheck as FactCheckIcon } from '@mui/icons-material';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { K9_OFFLINE_AUTH_V2_ENABLED } from '../../config/featureFlags';
import { isViteFlagEnabled } from '../../utils/runtimePlatformFlags';
import { isPerDiemEstimatorEnabled } from '../../utils/perDiemFeature';
import { useTheme } from '@mui/material/styles';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useLearningLab } from '../../contexts/LearningLabContext';
import { learningLabStickyHeaderTop } from '../../constants/learningLabLayout';
import { TOUR_DOM_EVENTS, type NavTourDropdownMenu } from '../onboarding/onboardingDomEvents';
import { getProfileImageUrl } from '../../utils/imageUtils';
import { getUserAvatar, handleImageError } from '../../utils/imageUtils';
import UnifiedNotificationDropdown from '../notifications/UnifiedNotificationDropdown';
import ResponsiveLogo from '../common/ResponsiveLogo';
import ThemeToggle from '../common/ThemeToggle';
import { LearningLabNavControl } from '../learning-lab/LearningLabNavControl';
import { getDevMode } from '../../utils/localStorageCache';
import { useTacticoreAuthoringChromeActive } from '../../hooks/useTacticoreAuthoringChromeActive';
import TacticoreAuthoringChrome from '../tacticore/TacticoreAuthoringChrome';
import ImpersonationStatus from '../common/ImpersonationStatus';
import adminPermissionService from '../../services/adminPermissionService';
import { isSessionAuthenticated, isSessionAuthenticatedAsync, getUserData } from '../../utils/storageUtils';
import { getAuthUrl, getMainDomainUrl } from '../../utils/subdomainUtils';
import { isDevPublicSiteHost } from '../../../shared/urls/publicSiteHost';
import { safeStorage, safeAPICall } from '../../utils/trackingUtils';
import { getNavigationUrl } from '../../utils/subdomainUtils';
import { PROFILE_AND_SETTINGS_NAV_PATH } from '../profile/profileSettingsTabs';
import SessionCountdown from './SessionCountdown';
import HeaderAccountUtilities from './HeaderAccountUtilities';
import { getSiteHeaderAppBarSx, headerMobileMenuButtonSx } from '../../constants/headerChromeStyles';
import { logger } from "../../utils/logger";
import { NOTIFICATIONS_CHANGED_EVENT } from '../../utils/notificationEvents';
import { toHttpHrefAttribute } from '../../../shared/utils/sanitizeHttpUrlForHref';
import { APEX_K9_MARK, APEX_K9_NAME } from '../../../shared/branding/apexK9';
import { AMERICON_MARK } from '../../../shared/branding/americon';
import {
  buildPublicToolsNavItems,
  isPublicToolsNavVisible,
} from '../../../shared/tcole/publicToolsNav';
import {
    CONVERSION_DOOR_ALERTS_LABEL,
    CONVERSION_DOOR_FIND_LABEL,
    CONVERSION_DOOR_HOST_LABEL,
    getConversionAlertsHref,
    getConversionFindHref,
    getConversionHostHref,
} from '../../../shared/conversion/conversionDoorsCopy';
import { SanitizedHrefButton } from '../common/SanitizedHrefAnchors';
import VisuallyHiddenText from '../common/VisuallyHiddenText';
import { useSubscription } from '../../hooks/useSubscription';
import USAFlagIcon from '../icons/USAFlagIcon';
import userService from '../../services/userService';
import {
    AGENCY_SHELL_HORIZONTAL_PX,
    HEADER_TOOLBAR_MIN_HEIGHT,
    HEADER_DESKTOP_NAV_FONT_SIZE,
    SITE_HEADER_HEIGHT_CSS_VAR,
} from '../../constants/layoutSpacing';

import { ApexK9TitleWithTm } from '../../utils/apexK9Branding';

/** Visible main-nav label with superscript ™ */
const apexK9HeaderNavLabel = <ApexK9TitleWithTm scale="nav" />;

// Reusable BadgeIcon component to eliminate duplicate badge logic
interface BadgeIconProps {
    children: React.ReactNode;
    badgeContent?: number;
    color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    overlap?: 'rectangular' | 'circular';
    anchorOrigin?: {
        vertical: 'top' | 'bottom';
        horizontal: 'left' | 'right';
    };
    sx?: any;
}
const BadgeIcon: React.FC<BadgeIconProps> = ({ children, badgeContent, color = 'error', overlap = 'circular', anchorOrigin = { vertical: 'top', horizontal: 'right' }, sx = {} }) => {
    const defaultSx = {
        '& .MuiBadge-badge': {
            backgroundColor: color === 'error' ? '#d32f2f' : undefined,
            color: 'white',
            fontSize: '11px',
            fontWeight: 600,
            minWidth: '18px',
            height: '18px',
            borderRadius: '9px',
            border: '2px solid white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }
    };
    return (<Badge badgeContent={badgeContent && badgeContent > 0 ? badgeContent : undefined} color={color} overlap={overlap} anchorOrigin={anchorOrigin} sx={{ ...defaultSx, ...sx }}>
      {children}
    </Badge>);
};
/** Normalize nav labels so "About Us" and "About\u00a0Us" share one dropdown key (fixes Menu anchor + ref mismatch). */
function getNavDropdownKey(label: string): string {
    return label.toLowerCase().replace(/\s+/g, '');
}

type NavDrawerState = {
    open: Record<string, boolean>;
    anchors: Record<string, HTMLElement | null>;
};

type NavDrawerAction =
    | { type: 'toggle'; key: string; menuButtonEl?: HTMLElement | null }
    | { type: 'open'; key: string; menuButtonEl: HTMLElement | null }
    | { type: 'close'; key: string }
    | { type: 'closeAll' };

function navDrawerReducer(state: NavDrawerState, action: NavDrawerAction): NavDrawerState {
    switch (action.type) {
        case 'toggle': {
            const wasOpen = state.open[action.key] ?? false;
            const nextOpen = !wasOpen;
            const newOpen = { ...state.open };
            const newAnchors = { ...state.anchors };

            if (nextOpen) {
                for (const k of Object.keys(newOpen)) {
                    if (k !== action.key && newOpen[k]) {
                        newOpen[k] = false;
                        newAnchors[k] = null;
                    }
                }
            }

            newOpen[action.key] = nextOpen;
            if (action.menuButtonEl != null) {
                newAnchors[action.key] = nextOpen ? action.menuButtonEl : null;
            }
            else if (!nextOpen) {
                newAnchors[action.key] = null;
            }

            return { open: newOpen, anchors: newAnchors };
        }
        case 'open': {
            if (
                state.open[action.key] &&
                state.anchors[action.key] === action.menuButtonEl
            ) {
                return state;
            }

            const newOpen = { ...state.open };
            const newAnchors = { ...state.anchors };

            for (const k of Object.keys(newOpen)) {
                if (k !== action.key) {
                    newOpen[k] = false;
                    newAnchors[k] = null;
                }
            }

            newOpen[action.key] = true;
            newAnchors[action.key] = action.menuButtonEl;

            return { open: newOpen, anchors: newAnchors };
        }
        case 'close': {
            return {
                open: { ...state.open, [action.key]: false },
                anchors: { ...state.anchors, [action.key]: null },
            };
        }
        case 'closeAll': {
            const newOpen = { ...state.open };
            const newAnchors = { ...state.anchors };
            for (const k of Object.keys(newOpen)) {
                if (newOpen[k]) {
                    newOpen[k] = false;
                    newAnchors[k] = null;
                }
            }
            return { open: newOpen, anchors: newAnchors };
        }
        default:
            return state;
    }
}
const Header: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const { active: learningLabActive } = useLearningLab();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    /** Single state object keeps Menu `open` and `anchorEl` in sync (avoids Popper defaulting when anchor lags). */
    const [navDrawer, dispatchNavDrawer] = useReducer(navDrawerReducer, { open: {}, anchors: {} });
    // Refs for polling intervals to ensure proper cleanup
    const messageIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const notificationIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const appBarRef = useRef<HTMLElement | null>(null);
    // Get user state from auth context
    const { isAuthenticated, user, isLoading, isInitializing } = useAuth();

    // Get onboarding state to prevent menus from closing during tours
    const { state: onboardingState } = useOnboarding();

    const tacticoreAuthoringChrome = useTacticoreAuthoringChromeActive();

    // Publish live AppBar height for full-viewport shells (Studio builder, etc.).
    useLayoutEffect(() => {
        if (tacticoreAuthoringChrome) {
            return;
        }
        const node = appBarRef.current;
        if (!node || typeof ResizeObserver === 'undefined') {
            return;
        }
        const root = document.documentElement;
        const syncHeight = () => {
            root.style.setProperty(SITE_HEADER_HEIGHT_CSS_VAR, `${Math.round(node.getBoundingClientRect().height)}px`);
        };
        syncHeight();
        const observer = new ResizeObserver(syncHeight);
        observer.observe(node);
        return () => {
            observer.disconnect();
            root.style.removeProperty(SITE_HEADER_HEIGHT_CSS_VAR);
        };
    }, [tacticoreAuthoringChrome]);

    // Debug auth state changes
    useEffect(() => {
        logger.security('🔐 Header Auth State:', { "arg0": {
                isAuthenticated,
                user: user ? { id: user.id, email: user.email, first_name: user.first_name } : null,
                isLoading,
                isInitializing,
                hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
                pathname: typeof window !== 'undefined' ? window.location.pathname : 'unknown'
            } });
    }, [isAuthenticated, user, isLoading, isInitializing]);
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);
    const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
    
    // Helper functions for dropdown management
    const toggleDrawerDropdown = useCallback((key: string, menuButtonEl?: HTMLElement | null) => {
        dispatchNavDrawer({ type: 'toggle', key, menuButtonEl });
    }, []);
    const isDrawerDropdownOpen = useCallback((key: string) => Boolean(navDrawer.open[key]), [navDrawer.open]);
    const closeDrawerDropdown = useCallback((key: string) => {
        dispatchNavDrawer({ type: 'close', key });
    }, []);

    useEffect(() => {
        const handleOpenNavDropdown = (event: Event) => {
            const menu = (event as CustomEvent<{ menu?: NavTourDropdownMenu }>).detail
                ?.menu;
            if (!menu) {
                return;
            }

            if (menu === 'browse' || menu === 'aboutus' || menu === 'products' || menu === 'tools') {
                const btn = document.getElementById(`${menu}-menu-button`);
                if (!btn) {
                    return;
                }
                dispatchNavDrawer({
                    type: 'open',
                    key: menu,
                    menuButtonEl: btn,
                });
            }
        };

        const handleCloseNavDropdowns = () => {
            dispatchNavDrawer({ type: 'closeAll' });
            setMobileOpen(false);
        };

        window.addEventListener(
            TOUR_DOM_EVENTS.OPEN_NAV_DROPDOWN,
            handleOpenNavDropdown,
        );
        window.addEventListener(
            TOUR_DOM_EVENTS.CLOSE_NAV_DROPDOWNS,
            handleCloseNavDropdowns,
        );

        return () => {
            window.removeEventListener(
                TOUR_DOM_EVENTS.OPEN_NAV_DROPDOWN,
                handleOpenNavDropdown,
            );
            window.removeEventListener(
                TOUR_DOM_EVENTS.CLOSE_NAV_DROPDOWNS,
                handleCloseNavDropdowns,
            );
        };
    }, []);

    const focusTourTooltip = () => {
        const tooltip = document.querySelector('[data-tour-tooltip="true"]') as HTMLElement | null;
        tooltip?.focus();
    };
    
    // Custom click handler to close Menu dropdowns on outside click
    // Material-UI's built-in click-away isn't working due to Google Analytics interference
    useEffect(() => {
        const hasOpenMenu = Object.values(navDrawer.open).some(open => open);
        if (!hasOpenMenu) {
            return;
        }

        const handleDocumentClick = (e: MouseEvent) => {
            const target = e.target as Element;
            if (!target) return;

            // Skip clicks on Menu buttons themselves
            if (target.closest('[id$="-menu-button"]') !== null) {
                return;
            }

            // CRITICAL: Skip clicks on AdSense elements to avoid interfering with ad functionality
            // This prevents dropdown handlers from blocking AdSense click detection
            if (target.closest('.adsbygoogle, [data-ad-client], [data-ad-slot], [data-ad-format], ins.adsbygoogle, [id*="google_ads"], [id*="goog-"], [id*="aswift_"]') !== null) {
                return; // Don't interfere with AdSense clicks
            }

            // Find the menu element
            const menu = document.querySelector('[role="menu"]');
            if (menu) {
                const rect = menu.getBoundingClientRect();
                const clickX = e.clientX;
                const clickY = e.clientY;
                
                // Check if click is outside the menu
                if (clickX < rect.left || clickX > rect.right || 
                    clickY < rect.top || clickY > rect.bottom) {
                    // Don't close dropdowns if a tour is running
                    if (!onboardingState.isRunning) {
                        // Click is outside menu - close all dropdowns
                        Object.keys(navDrawer.open).forEach(key => {
                            if (navDrawer.open[key]) {
                                closeDrawerDropdown(key);
                            }
                        });
                    }
                }
            } else {
                // No menu found, but dropdowns are open - close them
                // Don't close dropdowns if a tour is running
                if (!onboardingState.isRunning) {
                    Object.keys(navDrawer.open).forEach(key => {
                        if (navDrawer.open[key]) {
                            closeDrawerDropdown(key);
                        }
                    });
                }
            }
        };

        // Use mousedown instead of click to catch the event earlier
        document.addEventListener('mousedown', handleDocumentClick);
        // With disableScrollLock, page scroll can leave the menu orphaned from its sticky trigger.
        const handleScrollClose = (event: Event) => {
            if (onboardingState.isRunning) return;
            const target = event.target;
            if (
                target instanceof Element &&
                target.closest('.MuiMenu-root, .MuiPopover-root, [role="menu"]')
            ) {
                return;
            }
            Object.keys(navDrawer.open).forEach(key => {
                if (navDrawer.open[key]) {
                    closeDrawerDropdown(key);
                }
            });
        };
        window.addEventListener('scroll', handleScrollClose, { passive: true, capture: true });
        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
            window.removeEventListener('scroll', handleScrollClose, true);
        };
    }, [navDrawer.open, onboardingState.isRunning, closeDrawerDropdown]);
    // Fetch unread message count - optimized for performance
    const fetchUnreadMessageCount = async () => {
        if (!isAuthenticated || !user?.id) {
            setUnreadMessageCount(0);
            return;
        }
        try {
            const isAuth = await isSessionAuthenticatedAsync();
            if (!isAuth) {
                setUnreadMessageCount(0);
                return;
            }
            const result = await safeAPICall(async () => {
                const response = await fetch('/api/messaging/messages/unread-count', {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    // Add timeout to prevent hanging
                    signal: AbortSignal.timeout(5000)
                });
                if (response.ok) {
                    const data = await response.json();
                    return data.count || 0;
                }
                else if (response.status === 401 || response.status === 403) {
                    return 0;
                }
                else if (response.status >= 500) {
                    logger.error('Server error fetching unread message count, setting to 0', undefined, {});
                    return 0;
                }
                else {
                    return 0;
                }
            }, 0, // fallback value
            'Unread Messages');
            setUnreadMessageCount(result);
        }
        catch (error) {
            // Silently handle errors to prevent console spam
            setUnreadMessageCount(0);
        }
    };
    // Fetch combined unread count (notifications + messages) - optimized for performance
    const fetchUnreadNotificationCount = async () => {
        if (!isAuthenticated || !user?.id) {
            setUnreadNotificationCount(0);
            return;
        }
        try {
            const isAuth = await isSessionAuthenticatedAsync();
            if (!isAuth) {
                setUnreadNotificationCount(0);
                return;
            }
            const result = await safeAPICall(async () => {
                const response = await fetch('/api/notifications/unified?limit=1', {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    // Add timeout to prevent hanging
                    signal: AbortSignal.timeout(5000)
                });
                if (response.ok) {
                    const data = await response.json();
                    // Return combined unread count (notifications + messages)
                    return data.data?.counts?.unreadTotal || 0;
                }
                else if (response.status === 401 || response.status === 403) {
                    return 0;
                }
                else if (response.status >= 500) {
                    logger.error('Server error fetching unread count, setting to 0', undefined, {});
                    return 0;
                }
                else {
                    return 0;
                }
            }, 0, // fallback value
            'Unread Notifications & Messages');
            setUnreadNotificationCount(result);
        }
        catch (error) {
            // Silently handle errors to prevent console spam
            setUnreadNotificationCount(0);
        }
    };
    // Handle keyboard events to close dropdowns (ESC key)
    // Note: MUI Menu components handle backdrop clicks automatically via onClose prop
    // We only need to handle ESC key explicitly for better UX
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                dispatchNavDrawer({ type: 'closeAll' });
                if (onboardingState.isRunning) {
                    requestAnimationFrame(focusTourTooltip);
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onboardingState.isRunning]);
    // Refs to prevent duplicate requests from React Strict Mode double-mounting
    const hasFetchedCountsRef = useRef<boolean>(false);
    const fetchCountsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // Fetch counts when user changes - NON-BLOCKING with performance optimization
    useEffect(() => {
        // Only proceed if authentication is fully initialized and user is authenticated
        // Wait for initialization to complete to prevent 401 errors after login
        // Also prevent duplicate requests from React Strict Mode
        if (isAuthenticated && user?.id && !isLoading && !isInitializing && !hasFetchedCountsRef.current) {
            // Clear any existing timeout (React Strict Mode protection)
            if (fetchCountsTimeoutRef.current) {
                clearTimeout(fetchCountsTimeoutRef.current);
            }
            // Initial fetch - use setTimeout to avoid blocking
            // Increased delay to 1500ms to allow session cookie propagation across subdomains
            // This prevents 401 errors and rate limiting from too many simultaneous requests
            fetchCountsTimeoutRef.current = setTimeout(async () => {
                hasFetchedCountsRef.current = true;
                // Validate session before making API calls to prevent 401 errors
                const isAuth = await isSessionAuthenticatedAsync();
                if (isAuth) {
                    // Stagger the two calls slightly to avoid rate limiting
                    fetchUnreadMessageCount();
                    setTimeout(() => {
                        fetchUnreadNotificationCount();
                    }, 300);
                }
            }, 1500);
            // Set up polling with reduced frequency
            messageIntervalRef.current = setInterval(() => {
                fetchUnreadMessageCount();
            }, 300000); // 5 minutes
            notificationIntervalRef.current = setInterval(() => {
                fetchUnreadNotificationCount();
            }, 300000); // 5 minutes
            return () => {
                // Clear timeout and intervals on cleanup
                if (fetchCountsTimeoutRef.current) {
                    clearTimeout(fetchCountsTimeoutRef.current);
                    fetchCountsTimeoutRef.current = null;
                }
                if (messageIntervalRef.current) {
                    clearInterval(messageIntervalRef.current);
                    messageIntervalRef.current = null;
                }
                if (notificationIntervalRef.current) {
                    clearInterval(notificationIntervalRef.current);
                    notificationIntervalRef.current = null;
                }
            };
        }
        else {
            // Clear counts when user is not authenticated or still loading
            setUnreadMessageCount(0);
            setUnreadNotificationCount(0);
            hasFetchedCountsRef.current = false; // Reset flag when user logs out
        }
    }, [isAuthenticated, user?.id, isLoading, isInitializing]);
    useEffect(() => {
        if (!isAuthenticated || !user?.id) {
            return;
        }
        const onChanged = () => {
            void fetchUnreadNotificationCount();
        };
        window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
        return () => {
            window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
        };
    }, [isAuthenticated, user?.id]);
    const closeMobileDrawer = useCallback(() => {
        setMobileOpen(false);
    }, []);
    /** Backdrop / outside-tap / Escape — blocked while a tour is running (matches nav dropdown behavior). */
    const handleDrawerDismiss = useCallback(
        (_event?: React.SyntheticEvent | {}, _reason?: string) => {
            if (onboardingState.isRunning) {
                return;
            }
            closeMobileDrawer();
        },
        [onboardingState.isRunning, closeMobileDrawer],
    );
    const handleDrawerToggle = useCallback(() => {
        setMobileOpen((prev) => !prev);
    }, []);

    const prevMobileOpenRef = useRef(false);
    useEffect(() => {
        if (prevMobileOpenRef.current && !mobileOpen) {
            dispatchNavDrawer({ type: 'closeAll' });
        }
        prevMobileOpenRef.current = mobileOpen;
    }, [mobileOpen]);

    // MUI Drawer backdrop clicks are unreliable on some mobile browsers (GA / overlay interference).
    useEffect(() => {
        if (!mobileOpen) {
            return;
        }

        const handlePointerOutside = (event: PointerEvent) => {
            if (onboardingState.isRunning) {
                return;
            }

            const target = event.target as Element | null;
            if (!target) {
                return;
            }

            if (target.closest('[data-tour-stable="nav-mobile-menu"]') !== null) {
                return;
            }

            const drawerPaper = document.querySelector('[data-nav-drawer="header-mobile"]');
            if (drawerPaper?.contains(target)) {
                return;
            }

            handleDrawerDismiss();
            // Prevent the subsequent click from activating content under the backdrop on touch devices.
            if (event.cancelable) {
                event.preventDefault();
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleDrawerDismiss();
            }
        };

        document.addEventListener('pointerdown', handlePointerOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [mobileOpen, onboardingState.isRunning, handleDrawerDismiss]);
    const vendorDirectoryEnabled = isViteFlagEnabled(
        'VITE_VENDOR_DIRECTORY_ENABLED',
        import.meta.env.VITE_VENDOR_DIRECTORY_ENABLED,
    );
    const credentialCatalogNavEnabled = isViteFlagEnabled(
        'VITE_TICS_CREDENTIAL_CATALOG_ENABLED',
        import.meta.env.VITE_TICS_CREDENTIAL_CATALOG_ENABLED,
    );
    /** Same client gate as `/travel` CTA and `/travel/per-diem` (NotFound when off). */
    const perDiemNavEnabled = isPerDiemEstimatorEnabled();
    const conversionDoorsNavEnabled =
        import.meta.env.VITE_CONVERSION_DOORS_NAV_ENABLED === 'true';
    const tcoleNavEnabled = isViteFlagEnabled(
        'VITE_TCOLE_PSR_TOOL_ENABLED',
        import.meta.env.VITE_TCOLE_PSR_TOOL_ENABLED,
    );
    const scorecardNavEnabled = isViteFlagEnabled(
        'VITE_SCORECARD_READINESS_ENABLED',
        import.meta.env.VITE_SCORECARD_READINESS_ENABLED,
    );
    const toolsNavFlags = {
        tcoleEnabled: tcoleNavEnabled,
        scorecardEnabled: scorecardNavEnabled,
    };
    const toolsNavEnabled = isPublicToolsNavVisible(toolsNavFlags);
    const toolsNavItems = buildPublicToolsNavItems(toolsNavFlags);
    // Memoize navigation items to prevent unnecessary re-renders
    const navigationItems = useMemo(() => [
        { text: 'Home', path: getNavigationUrl('/'), icon: <HomeIcon /> },
        {
            text: 'Tac-Shop',
            path: getNavigationUrl('/tac-shop'),
            icon: <ShoppingCart />,
            hasDropdown: false
        },
        {
            text: 'Products',
            path: '#',
            icon: <ExtensionIcon />,
            hasDropdown: true,
            dropdownItems: [
                { text: 'TactiCore™', path: getNavigationUrl('/tacticore'), icon: <Computer fontSize="small" /> },
                { text: APEX_K9_MARK, path: getNavigationUrl('/k9-ops'), icon: <Pets fontSize="small" /> },
                {
                    text: 'APT Studio',
                    path: getNavigationUrl('/apt-studio'),
                    icon: <WebIcon fontSize="small" />,
                    children: [
                        {
                            text: 'ChatAPT',
                            path: getNavigationUrl('/chatapt'),
                            icon: <ChatBubbleOutlineIcon fontSize="small" />,
                        },
                    ],
                },
                { text: AMERICON_MARK, path: getNavigationUrl('/americon'), icon: <EventIcon fontSize="small" /> },
                { text: 'MyAPT', path: getNavigationUrl('/myapt'), icon: <PhoneIphoneIcon fontSize="small" /> },
                { text: 'TeachAPT', path: getNavigationUrl('/teachapt'), icon: <PhoneIphoneIcon fontSize="small" /> },
            ],
        },
        ...(toolsNavEnabled
            ? [
                {
                    text: 'Tools',
                    path: '#',
                    icon: <HandymanIcon />,
                    hasDropdown: true,
                    dropdownItems: toolsNavItems.map((item) => ({
                        text: item.text,
                        path: getNavigationUrl(item.path),
                        icon:
                            item.id === 'tcole-psr' ? (
                                <FactCheckIcon fontSize="small" />
                            ) : (
                                <AssessmentIcon fontSize="small" />
                            ),
                    })),
                },
            ]
            : []),
        ...(conversionDoorsNavEnabled ? [
            {
                text: CONVERSION_DOOR_FIND_LABEL,
                path: getNavigationUrl(getConversionFindHref()),
                icon: <SearchIcon />,
                hasDropdown: false,
                dataTestId: 'nav-door-find',
            },
            {
                text: CONVERSION_DOOR_HOST_LABEL,
                path: getNavigationUrl(getConversionHostHref()),
                icon: <Business />,
                hasDropdown: false,
                dataTestId: 'nav-door-host',
            },
            {
                text: CONVERSION_DOOR_ALERTS_LABEL,
                path: getNavigationUrl(getConversionAlertsHref()),
                icon: <NotificationsIcon />,
                hasDropdown: false,
                dataTestId: 'nav-door-alerts',
            },
        ] : []),
        {
            text: 'Browse',
            path: '#',
            icon: <Business />,
            hasDropdown: true,
            dropdownItems: [
                { text: 'All Events', path: getNavigationUrl('/events'), icon: <EventIcon fontSize="small" /> },
                { text: 'Agencies', path: getNavigationUrl('/agencies'), icon: <Business fontSize="small" /> },
                { text: 'Venues', path: getNavigationUrl('/venues'), isExternal: false, icon: <PlaceIcon fontSize="small" /> },
                ...(vendorDirectoryEnabled
                    ? [{ text: 'Vendors', path: getNavigationUrl('/vendors'), icon: <StoreIcon fontSize="small" /> }]
                    : []),
                { text: 'Course Catalog', path: getNavigationUrl('/courses'), icon: <MenuBookIcon fontSize="small" /> },
                ...(credentialCatalogNavEnabled
                    ? [{ text: 'Credentials', path: getNavigationUrl('/credentials'), icon: <VerifiedIcon fontSize="small" /> }]
                    : []),
                { text: 'People', path: getNavigationUrl('/people'), icon: <PeopleIcon fontSize="small" /> },
                { text: 'Photo Gallery', path: getNavigationUrl('/gallery'), icon: <GalleryIcon fontSize="small" /> },
                { text: 'LEO Recommended Restaurants', path: getNavigationUrl('/eat'), icon: <RestaurantIcon fontSize="small" /> },
                { text: 'Secondary Employment', path: getNavigationUrl('/secondary-employment'), icon: <WorkOutlineIcon fontSize="small" /> },
                { text: 'Knowledge Base', path: getNavigationUrl('/knowledge'), icon: <AutoStoriesIcon fontSize="small" /> },
            ]
        },
        { text: 'Blog', path: getNavigationUrl('/blog'), icon: <BlogIcon />, hasDropdown: false },
        ...(perDiemNavEnabled
            ? [
                {
                    text: 'Travel',
                    path: '#',
                    icon: <HotelIcon />,
                    hasDropdown: true,
                    dropdownItems: [
                        {
                            text: 'Travel & stays',
                            path: getNavigationUrl('/travel'),
                            icon: <HotelIcon fontSize="small" />,
                        },
                        {
                            text: 'Per diem calculator',
                            path: getNavigationUrl('/travel/per-diem'),
                            icon: <CalculateIcon fontSize="small" />,
                        },
                    ],
                },
            ]
            : [
                {
                    text: 'Travel',
                    path: getNavigationUrl('/travel'),
                    icon: <HotelIcon />,
                    hasDropdown: false,
                },
            ]),
        // Conditionally show About Us for guests or Leaderboard for logged-in users
        ...(isAuthenticated ? [
            { text: 'Leaderboard', path: getNavigationUrl('/leaderboard'), icon: <EmojiEvents />, hasDropdown: false }
        ] : [
            {
                text: 'About Us',
                path: '#',
                icon: <Info />,
                hasDropdown: true,
                dropdownItems: [
                    { text: 'About Us', path: getNavigationUrl('/about'), icon: <Info fontSize="small" /> },
                    { text: 'Training standards (TICS)', path: getNavigationUrl('/standards/tics'), icon: <VerifiedIcon fontSize="small" /> },
                    { text: 'Contact', path: getNavigationUrl('/contact'), icon: <MailOutlineIcon fontSize="small" /> },
                    { text: 'Privacy Policy', path: getNavigationUrl('/privacy'), icon: <PrivacyTipIcon fontSize="small" /> },
                    { text: 'Terms of Service', path: getNavigationUrl('/terms'), icon: <DescriptionIcon fontSize="small" /> },
                    { text: 'Cookie Policy', path: getNavigationUrl('/cookie-policy'), icon: <CookieIcon fontSize="small" /> },
                ]
            }
        ]),
    ], [
        conversionDoorsNavEnabled,
        isAuthenticated,
        perDiemNavEnabled,
        toolsNavEnabled,
        toolsNavItems,
        credentialCatalogNavEnabled,
        vendorDirectoryEnabled,
    ]);
    // Memoize user menu items to prevent unnecessary re-renders
    const userMenuItems = useMemo(() => [
        { text: 'Dashboard', path: getNavigationUrl('/dashboard'), icon: <DashboardIcon /> },
        {
            text: 'Notifications',
            path: getNavigationUrl('/notifications'),
            icon: <NotificationsIcon />,
            badgeContent: unreadNotificationCount
        },
        {
            text: 'Messages',
            path: getNavigationUrl('/messages'),
            icon: <MessageIcon />,
            badgeContent: unreadMessageCount
        },
        { text: 'My Blogs', path: getNavigationUrl('/blog/manage'), icon: <BlogIcon /> },
        { text: 'My Favorites', path: getNavigationUrl('/favorites'), icon: <Favorite /> },
        { text: 'Profile & Settings', path: getNavigationUrl(PROFILE_AND_SETTINGS_NAV_PATH), icon: <Person /> },
    ], [unreadNotificationCount, unreadMessageCount]);
    const renderNavigationItem = useCallback((item: any) => {
        // Check if the path is an external URL (starts with http)
        const isExternal = item.path.startsWith('http');
        // Generate data-tour ID from item text
        const tourId = item.text.toLowerCase().replace(/\s+/g, '-').replace(/™/g, '').replace(/[^a-z0-9-]/g, '');
        const itemPathname = (() => {
            try {
                if (isExternal) {
                    return new URL(item.path).pathname;
                }
            } catch {
                /* fall through */
            }
            return typeof item.path === 'string' ? item.path : '';
        })();
        const isShopNavActive =
            itemPathname.includes('/tac-shop') &&
            (location.pathname === '/tac-shop' ||
                location.pathname.startsWith('/tac-shop/'));
        
        if (item.hasDropdown) {
            const dropdownKey = getNavDropdownKey(item.text);
            // Keep dropdown open once opened during tours (don't force-open all menus)
            const baseIsOpen = isDrawerDropdownOpen(dropdownKey);
            const isOpen = baseIsOpen;
            return (<Box key={item.text} sx={{ position: 'relative' }}>
          <Button 
            id={`${dropdownKey}-menu-button`} 
            data-tour={`nav-${tourId}-button`}
            data-tour-stable={
              item.text === 'Browse'
                ? 'nav-browse-button'
                : item.text === 'About Us'
                  ? 'nav-about-us-button'
                  : item.text === 'Products'
                    ? 'nav-products-button'
                    : item.text === 'Tools'
                      ? 'nav-tools-button'
                      : undefined
            }
            color="inherit" 
            startIcon={item.icon} 
            endIcon={isOpen ? <ExpandLess /> : <ExpandMore />} 
            onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDrawerDropdown(dropdownKey, e.currentTarget);
                }} 
            aria-haspopup="true" 
            aria-expanded={isOpen} 
            sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 0.5,
                    py: 0.375,
                    minWidth: 'auto',
                    whiteSpace: 'nowrap',
                    fontSize: HEADER_DESKTOP_NAV_FONT_SIZE,
                    flexShrink: 0,
                    '&:hover': {
                        backgroundColor: 'action.hover',
                    }
                }}>
            {item.text}
          </Button>
          <Menu anchorEl={navDrawer.anchors[dropdownKey] ?? null} open={isOpen && Boolean(navDrawer.anchors[dropdownKey])} onClose={(event: React.SyntheticEvent | {}, reason: string) => {
                    if (onboardingState.isRunning) {
                        if (reason === 'escapeKeyDown' || reason === 'tabKeyDown') {
                            closeDrawerDropdown(dropdownKey);
                            requestAnimationFrame(focusTourTooltip);
                        }
                        return;
                    }
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown' || reason === 'tabKeyDown' || reason === 'menuItemClick') {
                        closeDrawerDropdown(dropdownKey);
                    }
                }} anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }} transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }} sx={{
                    '& .MuiPaper-root': {
                        mt: 1,
                        minWidth: 220,
                        boxShadow: 3,
                    }
                }}
                // Keep sticky AppBar pinned: Modal scroll-lock sets body overflow:hidden,
                // which makes position:sticky leave the viewport while the menu stays open.
                disableScrollLock
                disableAutoFocusItem={!onboardingState.isRunning} MenuListProps={{
                    'aria-labelledby': `${dropdownKey}-menu-button`,
                }}>
            {item.dropdownItems?.flatMap((dropdownItem: any) => {
                    const isDropdownExternal = dropdownItem.path.startsWith('http');
                    const dropdownTourId = dropdownItem.text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                    const closeOrBlockNav = (e: React.MouseEvent) => {
                            // Don't close menu if a tour is running (to allow tour steps to target dropdown items)
                            if (!onboardingState.isRunning) {
                                closeDrawerDropdown(dropdownKey);
                            } else {
                                // Prevent navigation during tour
                                e.preventDefault();
                                e.stopPropagation();
                            }
                        };
                    const parentItem = (
                      <MenuItem
                        key={dropdownItem.text}
                        data-tour={`nav-${tourId}-${dropdownTourId}`}
                        component={isDropdownExternal ? 'a' : Link}
                        href={isDropdownExternal ? dropdownItem.path : undefined}
                        to={isDropdownExternal ? undefined : dropdownItem.path}
                        onClick={closeOrBlockNav}
                        sx={{
                            px: 2,
                            py: 1.5,
                            gap: 1.5,
                        }}>
                  {dropdownItem.icon ? (
                    <ListItemIcon sx={{ minWidth: 28, color: 'text.secondary' }}>
                      {dropdownItem.icon}
                    </ListItemIcon>
                  ) : null}
                  {dropdownItem.text}
                </MenuItem>
                    );
                    const nestedItems = (dropdownItem.children ?? []).map((child: any) => {
                        const isChildExternal = child.path.startsWith('http');
                        const childTourId = child.text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                        return (
                          <MenuItem
                            key={`${dropdownItem.text}-${child.text}`}
                            data-tour={`nav-${tourId}-${childTourId}`}
                            component={isChildExternal ? 'a' : Link}
                            href={isChildExternal ? child.path : undefined}
                            to={isChildExternal ? undefined : child.path}
                            onClick={closeOrBlockNav}
                            sx={{
                                pl: 4.5,
                                pr: 2,
                                py: 1.25,
                                gap: 1.5,
                                typography: 'body2',
                                color: 'text.secondary',
                            }}
                          >
                            {child.icon ? (
                              <ListItemIcon sx={{ minWidth: 28, color: 'text.secondary' }}>
                                {child.icon}
                              </ListItemIcon>
                            ) : null}
                            {child.text}
                          </MenuItem>
                        );
                    });
                    return [parentItem, ...nestedItems];
                })}
          </Menu>
        </Box>);
        }
        return (<Button 
            key={item.text} 
            data-testid={item.dataTestId}
            data-tour={`nav-${tourId}`}
            data-tour-stable={item.text === 'Home' ? 'nav-home' : undefined}
            component={isExternal ? 'a' : Link} 
            href={isExternal ? item.path : undefined} 
            to={isExternal ? undefined : item.path} 
            color="inherit" 
            startIcon={item.icon}
            aria-current={isShopNavActive ? 'page' : undefined}
            sx={{
                textTransform: 'none',
                fontWeight: isShopNavActive ? 700 : 600,
                color: isShopNavActive ? 'primary.main' : 'text.primary',
                px: 0.5,
                py: 0.375,
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                fontSize: HEADER_DESKTOP_NAV_FONT_SIZE,
                flexShrink: 0,
                borderBottom: isShopNavActive ? '2px solid' : '2px solid transparent',
                borderColor: isShopNavActive ? 'primary.main' : 'transparent',
                borderRadius: 0,
                '&:hover': {
                    backgroundColor: 'action.hover',
                }
            }}>
        {item.text === APEX_K9_MARK ? apexK9HeaderNavLabel : item.text}
      </Button>);
    }, [isDrawerDropdownOpen, toggleDrawerDropdown, closeDrawerDropdown, navDrawer, onboardingState.isRunning, location.pathname]);
    const drawer = (<Box>
      {/* Mobile Drawer Header with Logo */}
      <Box sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        }}>
        {/* wide={true} applies to desktop full lockup; mobile icon uses square apt-icon-only assets (Logo forces wide off for variant icon). */}
        <ResponsiveLogo mobileVariant="icon" tabletVariant="full" desktopVariant="full" mobileSize="small" tabletSize="medium" desktopSize="medium" transparent={true} wide={true} noBuffer={true} sx={{ height: { xs: 32, sm: 40 }, width: { xs: 'auto', sm: 'auto' }, maxWidth: { xs: 56, sm: 260 } }}/>
      </Box>
      <List>
        {navigationItems.map((item) => {
            // All navigation URLs are full URLs (with https://), so treat them all as external
            const isExternal = item.path.startsWith('http');
            const itemPathname = (() => {
                try {
                    if (isExternal) {
                        return new URL(item.path).pathname;
                    }
                } catch {
                    /* fall through */
                }
                return typeof item.path === 'string' ? item.path : '';
            })();
            const isShopNavActive =
                itemPathname.includes('/tac-shop') &&
                (location.pathname === '/tac-shop' ||
                    location.pathname.startsWith('/tac-shop/'));
            return (<React.Fragment key={item.text}>
              <ListItem data-testid={item.dataTestId} component={isExternal ? 'a' : 'div'} href={isExternal ? item.path : undefined} onClick={item.hasDropdown ? undefined : (isExternal ? undefined : closeMobileDrawer)} sx={{ color: isShopNavActive ? 'primary.main' : 'inherit', textDecoration: 'none', cursor: item.hasDropdown ? 'default' : 'pointer' }} aria-current={isShopNavActive ? 'page' : undefined}>
                <ListItemIcon sx={{ color: isShopNavActive ? 'primary.main' : undefined }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text === APEX_K9_MARK ? apexK9HeaderNavLabel : item.text} primaryTypographyProps={isShopNavActive ? { fontWeight: 700 } : undefined}/>
                {item.hasDropdown && (<IconButton onClick={(e) => {
                        e.preventDefault();
                        const dropdownKey = getNavDropdownKey(item.text);
                        toggleDrawerDropdown(dropdownKey);
                    }} aria-label={`${item.text} menu`} aria-haspopup="true" aria-expanded={isDrawerDropdownOpen(getNavDropdownKey(item.text))}>
                    {isDrawerDropdownOpen(getNavDropdownKey(item.text)) ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>)}
              </ListItem>
              {item.hasDropdown && (<Collapse in={isDrawerDropdownOpen(getNavDropdownKey(item.text))}>
                  <List component="div" disablePadding>
                    {item.dropdownItems?.flatMap((dropdownItem: any) => {
                        // All dropdown URLs are also full URLs
                        const parentTourId = item.text.toLowerCase().replace(/\s+/g, '-').replace(/™/g, '').replace(/[^a-z0-9-]/g, '');
                        const dropdownTourId = dropdownItem.text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                        const parentRow = (
                          <ListItem
                            key={dropdownItem.text}
                            data-tour={`nav-${parentTourId}-${dropdownTourId}`}
                            component="a"
                            href={dropdownItem.path}
                            onClick={closeMobileDrawer}
                            sx={{
                                pl: 4,
                                color: 'inherit',
                                textDecoration: 'none',
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                }
                            }}>
                          {dropdownItem.icon ? (
                            <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                              {dropdownItem.icon}
                            </ListItemIcon>
                          ) : null}
                          <ListItemText primary={dropdownItem.text}/>
                        </ListItem>
                        );
                        const childRows = (dropdownItem.children ?? []).map((child: any) => {
                          const childTourId = child.text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                          return (
                          <ListItem
                            key={`${dropdownItem.text}-${child.text}`}
                            data-tour={`nav-${parentTourId}-${childTourId}`}
                            component="a"
                            href={child.path}
                            onClick={closeMobileDrawer}
                            sx={{
                              pl: 7,
                              color: 'text.secondary',
                              textDecoration: 'none',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            {child.icon ? (
                              <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                                {child.icon}
                              </ListItemIcon>
                            ) : null}
                            <ListItemText
                              primary={child.text}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                          );
                        });
                        return [parentRow, ...childRows];
                    })}
                  </List>
                </Collapse>)}
            </React.Fragment>);
        })}
        {isAuthenticated && (<>
            <ListItem divider/>
            {userMenuItems.map((item) => {
                // All user menu URLs are full URLs, so use anchor tags for cross-subdomain navigation
                return (<ListItem key={item.text} component="a" href={item.path} onClick={closeMobileDrawer} sx={{ color: 'inherit', textDecoration: 'none' }}>
                  <ListItemIcon>
                    {item.badgeContent !== undefined ? (<BadgeIcon badgeContent={item.badgeContent} color={item.text === 'Messages' ? 'primary' : 'error'}>
                        {item.icon}
                      </BadgeIcon>) : (item.icon)}
                  </ListItemIcon>
                  <ListItemText primary={item.text}/>
                </ListItem>);
            })}
          </>)}
      </List>
    </Box>);
    if (tacticoreAuthoringChrome) {
      return <TacticoreAuthoringChrome />;
    }
    return (<>
      <AppBar
        ref={appBarRef}
        position="sticky"
        elevation={1}
        sx={getSiteHeaderAppBarSx(theme, learningLabStickyHeaderTop(learningLabActive))}
      >
        <Toolbar sx={{
            px: AGENCY_SHELL_HORIZONTAL_PX,
            minHeight: HEADER_TOOLBAR_MIN_HEIGHT,
            '& .MuiButton-root': { borderRadius: 0 },
            '& .MuiIconButton-root': { borderRadius: 0 },
          }}>
          {/* Mobile menu button */}
          <IconButton 
            data-tour="nav-mobile-menu"
            data-tour-stable="nav-mobile-menu"
            color="inherit" 
            aria-label="open drawer" 
            onClick={handleDrawerToggle} 
            sx={headerMobileMenuButtonSx}>
            <MenuIcon sx={{ fontSize: { xs: 20, sm: 22 } }}/>
          </IconButton>

                     {/* Logo/Brand */}
           <Box 
            component={Link} 
            to="/" 
            data-tour="nav-logo"
            data-tour-stable="nav-logo"
            onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            // Check if we're on a subdomain
            const hostname = window.location.hostname;
            const isLegacyProductSubdomain =
                hostname.includes('.') &&
                !hostname.startsWith('www.') &&
                hostname !== 'americanpolicetraining.com' &&
                hostname !== 'localhost' &&
                !isDevPublicSiteHost(hostname);
            if (isLegacyProductSubdomain) {
                // Navigate to main domain, preserving port in development
                const protocol = window.location.protocol;
                const port = window.location.port ? `:${window.location.port}` : '';
                const mainDomain = hostname.includes('localhost')
                    ? `${protocol}//localhost${port}`
                    : `${protocol}//americanpolicetraining.com${port}`;
                window.location.href = mainDomain;
            }
            else {
                // Navigate within same domain
                navigate('/');
            }
        }} sx={{
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            mr: { xs: 1, sm: 1.5, md: 2 },
            flexShrink: 0,
            cursor: 'pointer',
            pointerEvents: 'auto',
            position: 'relative',
            // Icon mark below md (ResponsiveLogo); full lockup from md up.
            // Do not reserve full-logo width at sm — phone landscape was centering
            // the square icon in a ~312px box and pushing it away from the menu.
            // Match Logo `mobileSize="small"` HTML attrs (40×40) to avoid CLS / LCP mismatch.
            height: { xs: 40, sm: 40, md: 56 },
            width: { xs: 40, sm: 40, md: 364 },
            maxWidth: '100%',
            '& img': {
                cursor: 'pointer',
                pointerEvents: 'auto',
                objectPosition: 'left center',
            }
        }}>
            {/* wide={true} for md+ full logo; xs/sm use square icon-only (see Logo). */}
            <ResponsiveLogo mobileVariant="icon" tabletVariant="full" desktopVariant="full" mobileSize="small" tabletSize="medium" desktopSize="large" transparent={true} wide={true} noBuffer={true} loading="eager" fetchPriority="high" sx={{
            height: '100%',
            width: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            objectPosition: 'left center',
            cursor: 'pointer',
            pointerEvents: 'auto',
        }}/>
            <VisuallyHiddenText>American Police Training</VisuallyHiddenText>
          </Box>

          {/* Desktop Navigation - wrap to a second row when needed (no horizontal scrollbar; same Header on all subdomains) */}
          {!isMobile && (<Box data-tour="header-nav" sx={{
                display: 'flex',
                gap: 0.5,
                flex: '0 1 auto',
                maxWidth: 'calc(100% - 280px)',
                justifyContent: 'flex-start',
                alignItems: 'center',
                minWidth: 0,
                flexWrap: 'wrap',
                rowGap: 0.5,
            }}>
              {navigationItems.map(renderNavigationItem)}
            </Box>)}

          <HeaderAccountUtilities />
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerDismiss} PaperProps={{
            'data-nav-drawer': 'header-mobile',
        }} ModalProps={{
            keepMounted: true, // Better open performance on mobile.
        }} sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}>
        {drawer}
      </Drawer>
    </>);
};
export default Header;
